/* eslint-disable i18next/no-literal-string -- dev-only command palette restricted to local/test environments; not user-facing UI */
import { useEffect, useRef, useState } from "react";

import {
  clearAllOverrides,
  isDevOverlayEnvironment,
  NON_OVERRIDABLE_FLAGS,
  setAllOverrides,
} from "@vega-api/extended/auth/featureFlagOverrides";
import { uiCapabilityKeys } from "@vega-api/extended/auth/uiCapabilities";
import {
  clearAllCapabilityOverrides,
  setAllCapabilityOverrides,
} from "@vega-api/extended/auth/uiCapabilityOverrides";
import { featureFlagKeys } from "@vega-api/extended/auth/useFeatureFlag";
import { VegaDialog } from "@vega-core/components/Modals/VegaDialog/VegaDialog";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { FeatureFlagPanel } from "./FeatureFlagPanel";
import { UiCapabilityPanel } from "./UiCapabilityPanel";

type DevCommand = {
  id: string;
  title: string;
  keywords: string[];
  run: () => void;
};

export function DevCommandPalette() {
  return isDevOverlayEnvironment() ? <DevCommandPaletteContent /> : null;
}

function DevCommandPaletteContent() {
  const {
    value: isPaletteOpen,
    setTrue: openPalette,
    setFalse: closePalette,
  } = useBoolean(false);
  const {
    value: isFfPanelOpen,
    setTrue: openFfPanel,
    setFalse: closeFfPanel,
  } = useBoolean(false);
  const {
    value: isCapabilityPanelOpen,
    setTrue: openCapabilityPanel,
    setFalse: closeCapabilityPanel,
  } = useBoolean(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const overridableKeys = featureFlagKeys.filter(
    (key) => !NON_OVERRIDABLE_FLAGS.has(key),
  );

  const commands: DevCommand[] = [
    {
      id: "feature-flags",
      title: "Feature Flags",
      keywords: ["ff", "flags", "toggle", "override"],
      run: () => {
        closePalette();
        openFfPanel();
      },
    },
    {
      id: "feature-flags-enable-all",
      title: "Force enable all feature flags",
      keywords: ["ff", "flags", "enable", "on", "all"],
      run: () => {
        setAllOverrides(overridableKeys, true);
        closePalette();
      },
    },
    {
      id: "feature-flags-disable-all",
      title: "Force disable all feature flags",
      keywords: ["ff", "flags", "disable", "off", "all"],
      run: () => {
        setAllOverrides(overridableKeys, false);
        closePalette();
      },
    },
    {
      id: "feature-flags-reset-all",
      title: "Reset all feature flag overrides",
      keywords: ["ff", "flags", "reset", "clear", "default"],
      run: () => {
        clearAllOverrides();
        closePalette();
      },
    },
    {
      id: "ui-capabilities",
      title: "UI Capabilities",
      keywords: ["uc", "caps", "capabilities", "permissions", "override"],
      run: () => {
        closePalette();
        openCapabilityPanel();
      },
    },
    {
      id: "ui-capabilities-enable-all",
      title: "Force enable all UI capabilities",
      keywords: ["uc", "caps", "capabilities", "enable", "on", "all"],
      run: () => {
        setAllCapabilityOverrides(uiCapabilityKeys, true);
        closePalette();
      },
    },
    {
      id: "ui-capabilities-disable-all",
      title: "Force disable all UI capabilities",
      keywords: ["uc", "caps", "capabilities", "disable", "off", "all"],
      run: () => {
        setAllCapabilityOverrides(uiCapabilityKeys, false);
        closePalette();
      },
    },
    {
      id: "ui-capabilities-reset-all",
      title: "Reset all UI capability overrides",
      keywords: ["uc", "caps", "capabilities", "reset", "clear", "default"],
      run: () => {
        clearAllCapabilityOverrides();
        closePalette();
      },
    },
  ];

  const needle = search.toLowerCase().trim();
  const filteredCommands = commands.filter((cmd) => {
    if (needle === "") return true;

    return (
      cmd.title.toLowerCase().includes(needle) ||
      cmd.keywords.some((kw) => kw.toLowerCase().includes(needle))
    );
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isShortcut =
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        (event.key === "p" || event.key === "P");

      if (isShortcut) {
        event.preventDefault();
        setSearch("");
        setHighlightedIndex(0);
        openPalette();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [openPalette]);

  useEffect(() => {
    if (isPaletteOpen) {
      const timer = setTimeout(() => searchRef.current?.focus(), 0);

      return () => clearTimeout(timer);
    }
  }, [isPaletteOpen]);

  useEffect(() => {
    if (highlightedIndex >= filteredCommands.length) {
      setHighlightedIndex(0);
    }
  }, [filteredCommands.length, highlightedIndex]);

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((i) =>
        filteredCommands.length === 0
          ? 0
          : Math.min(i + 1, filteredCommands.length - 1),
      );

      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));

      return;
    }

    if (
      event.key === "Enter" &&
      filteredCommands.length > 0 &&
      highlightedIndex < filteredCommands.length
    ) {
      event.preventDefault();
      filteredCommands[highlightedIndex].run();
    }
  }

  return (
    <>
      <VegaDialog
        dataTestId="dev-command-palette"
        hideCloseButton
        onClose={closePalette}
        open={isPaletteOpen}
        setOpen={closePalette}
      >
        <div className="flex flex-col gap-3 min-w-80">
          <input
            className="body-base-regular border border-border/primary rounded p-2 bg-background/primary text-text/primary outline-none focus:border-brand-default"
            data-testid="dev-command-palette-search"
            onChange={(event) => {
              setSearch(event.target.value);
              setHighlightedIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Search dev commands..."
            ref={searchRef}
            type="text"
            value={search}
          />
          <ul className="flex flex-col gap-1">
            {filteredCommands.length === 0 ? (
              <li className="px-3 py-2 body-base-regular text-text/secondary">
                No commands found
              </li>
            ) : (
              filteredCommands.map((cmd, index) => (
                <li key={cmd.id}>
                  <button
                    className={cnTwMerge(
                      "w-full text-left px-3 py-2 rounded body-base-regular text-text/primary",
                      index === highlightedIndex
                        ? "bg-background/secondary"
                        : "hover:bg-background/secondary",
                    )}
                    data-testid={`dev-command-${cmd.id}`}
                    onClick={cmd.run}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    type="button"
                  >
                    {cmd.title}
                  </button>
                </li>
              ))
            )}
          </ul>
          <div className="body-small-regular text-text/secondary">
            Dev tools (local/test only)
          </div>
        </div>
      </VegaDialog>

      <FeatureFlagPanel isOpen={isFfPanelOpen} onClose={closeFfPanel} />
      <UiCapabilityPanel
        isOpen={isCapabilityPanelOpen}
        onClose={closeCapabilityPanel}
      />
    </>
  );
}
